import {
    NavigationLinkAdvancedConfiguration, NavigationLinkAdvancedCrossBackMode,
    NavigationLinkAdvancedOpenMode, NavigationLinkBasicOpenMode,
    NavigationLinkBuilderStrategy,
} from "../configurations/config-data-sections/navigation-link.configuration";
import {Check} from "@sparkware/app-utils";
import {isAbsolutUrl} from "../../../utils/url-utils";
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {LinkNavigationId} from "../configurations/config-data-sections/generated-config-const";
import {UriPath} from "../location/uri-path/uri-path";

export class LinkConfigurationParser {
    constructor(public readonly linkId: LinkNavigationId,
                private readonly services: IServiceFactoryExtended) {
        const config = services.configuration.data.links[linkId];
        if(!config) {
            throw new Error(`Missing configuration for ${linkId} link`);
        }

        if(Check.isString(config)) {
            this.linkConfiguration = {
                url: config
            };
        } else {
            this.linkConfiguration = config;
        }

    }

    private readonly linkConfiguration: NavigationLinkAdvancedConfiguration;

    get url(): string {
        return this.linkConfiguration.url ?? "";
    }

    private _getMiniOrbitDefaultOpenMode(): NavigationLinkBasicOpenMode {
        if(this.urlBuilderStrategy === "useGNWP") {
            return "newTab";
        }

        if(isAbsolutUrl(this.url)) {
            const urlBuilder = new URL(this.url);
            if(urlBuilder.origin.toLowerCase() === this.services.window.origin.toLowerCase()) {
                return "redirect";
            } else {
                return "newTab";
            }
        } else {
            if(this.services.routing.isOrbitRoute(new UriPath(this.url))) {
                return "clientSideNavigation";
            } else {
                return "redirect";
            }
        }
    }

    get openMode(): NavigationLinkAdvancedOpenMode {
        if(!this.linkConfiguration.openMode) {
            if(this.urlBuilderStrategy === "useGNWP" || isAbsolutUrl(this.url)) {
                return {
                    pc: "newTab",
                    mobile: "newTab",
                    hybrid: "newTab",
                    miniOrbit: this._getMiniOrbitDefaultOpenMode()
                }
            } else {
                return {
                    pc: "clientSideNavigation",
                    mobile: "clientSideNavigation",
                    hybrid: "clientSideNavigation",
                    miniOrbit: this._getMiniOrbitDefaultOpenMode()
                }
            }

        }

        if(Check.isString(this.linkConfiguration.openMode)) {
            return {
                pc: this.linkConfiguration.openMode,
                mobile: this.linkConfiguration.openMode,
                hybrid: this.linkConfiguration.openMode,
                miniOrbit: this._getMiniOrbitDefaultOpenMode()
            }
        }

        return {
            pc: this.linkConfiguration.openMode.pc ?? "newTab",
            mobile: this.linkConfiguration.openMode.mobile ?? "newTab",
            hybrid: this.linkConfiguration.openMode.hybrid ?? "newTab",
            miniOrbit: this.linkConfiguration.openMode.miniOrbit ?? this._getMiniOrbitDefaultOpenMode()
        }
    }

    get crossBackMode(): NavigationLinkAdvancedCrossBackMode {
        if(!this.linkConfiguration.crossBackMode) {
            if(isAbsolutUrl(this.url)) {
                return {
                    pc: "redirect",
                    mobile: "redirect",
                    hybrid: "redirect"
                }
            } else {
                return {
                    pc: "clientSideNavigation",
                    mobile: "clientSideNavigation",
                    hybrid: "clientSideNavigation",
                }
            }

        }

        if(Check.isString(this.linkConfiguration.crossBackMode)) {
            return {
                pc: this.linkConfiguration.crossBackMode,
                mobile: this.linkConfiguration.crossBackMode,
                hybrid: this.linkConfiguration.crossBackMode,
            }
        }

        return {
            pc: this.linkConfiguration.crossBackMode.pc ?? "redirect",
            mobile: this.linkConfiguration.crossBackMode.mobile ?? "redirect",
            hybrid: this.linkConfiguration.crossBackMode.hybrid ?? "redirect",

        }
    }


    get urlBuilderStrategy(): NavigationLinkBuilderStrategy {
        if(this.linkConfiguration.urlBuilderStrategy) {
            return this.linkConfiguration.urlBuilderStrategy;
        }

        if(this.services.user.isAuthorized) {
            if(this._supportsGnwp) {
                return "useGNWP";
            } else {
                return "useUrl";
            }
        }

        return "useUrl";
    }

    get shouldAppendSCutToUrl(): boolean {
        if(Check.isNullOrUndefined(this.linkConfiguration.shouldAppendSCutToUrl)) {
            return isAbsolutUrl(this.url);
        }

        return this.linkConfiguration.shouldAppendSCutToUrl;
    }

    private get _supportsGnwp(): boolean {
        switch (this.linkId) {
            case "pokerLink":
            case "sportLink":
            case "chatLink":
            case "helpLink":
                return true;
            default:
                return false;
        }
    }

    async getGnwpUrl(marketingParams: MarketingParams): Promise<string> {
        switch (this.linkId) {
            case "pokerLink":
                return await this.services.gnwp.getPokerUrl({
                    marketingParams: marketingParams
                });
            case "sportLink":
                return await this.services.gnwp.getSportUrl({
                    marketingParams: marketingParams
                });
            case "chatLink":
                return await this.services.gnwp.getLiveChatUrl({
                    marketingParams: marketingParams
                });
            case "helpLink":
                return await this.services.gnwp.getHelpPageUrl();
            default:
                throw new Error(`Link ${this.linkId} does not support GNWP`);
        }
    }

    get isCrossBackToPokerOrSport(): boolean {
        return (this.linkId === "pokerLink" && this.services.crossBrands.isCrossFromPoker)
            || (this.linkId === "sportLink" && this.services.crossBrands.isCrossFromSport);
    }
}